import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MagnifyingGlassIcon,
  DocumentTextIcon,
  TrophyIcon,
  BanknotesIcon,
  ShieldCheckIcon,
  TruckIcon,
  DocumentCheckIcon,
  HomeIcon,
  MapPinIcon,
  ClipboardDocumentCheckIcon,
  IdentificationIcon,
  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const HowItWorksPage: React.FC = () => {
  const steps = [
    {
      number: 1,
      title: 'Изберете автомобил',
      description: 'Изберете вашия мечтан автомобил и ни изпратете линк, за да го проверим детайлно. Ние ще направим пълна проверка на историята, щетите и състоянието на автомобила.',
      icon: MagnifyingGlassIcon,
      color: 'bg-blue-500',
      lightColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      number: 2,
      title: 'Депозит',
      description: 'Оставете депозит за участие в аукциона. Депозитът е 10% от сумата, до която искате да наддавате. Депозитът не се губи и се връща ако колата не се спечели или се играе за следващата.',
      icon: DocumentTextIcon,
      color: 'bg-purple-500',
      lightColor: 'bg-purple-50',
      textColor: 'text-purple-600',
    },
    {
      number: 3,
      title: 'Търг',
      description: 'Можем да сложим автоматичен залог до определената сума или да наддаваме на живо заедно. При желание сме в постоянна връзка с вас докато тече търгът. Ако спечелим - преминаваме към следващата стъпка. Ако не - започваме отново.',
      icon: TrophyIcon,
      color: 'bg-amber-500',
      lightColor: 'bg-amber-50',
      textColor: 'text-amber-600'
    },
    {
      number: 4,
      title: 'Покупка и плащане',
      description: 'След като спечелите наддаването, заплащате продажната цена плюс таксите на търга в срок до 48 часа по банков път. Ние издаваме фактура за превода.',
      icon: BanknotesIcon,
      color: 'bg-emerald-500',
      lightColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
    },
    {
      number: 5,
      title: 'Застраховка',
      description: 'Предлагаме застраховка "Total loss" - дори и за автомобили със съществуващи щети. Защитете вашата инвестиция по време на транспорта.',
      icon: ShieldCheckIcon,
      color: 'bg-indigo-500',
      lightColor: 'bg-indigo-50',
      textColor: 'text-indigo-600'
    },
    {
      number: 6,
      title: 'Транспорт до Европа',
      description: 'Вземаме автомобила от съответната локация, товарим го на контейнер и организираме морския транспорт до Европа.',
      icon: TruckIcon,
      color: 'bg-cyan-500',
      lightColor: 'bg-cyan-50',
      textColor: 'text-cyan-600',
      highlight: '🚢 Доставка: 2-3 месеца'
    },
    {
      number: 7,
      title: 'Обмитяване',
      description: 'След като автомобилът пристигне в Европа, ние ви съдействаме изцяло с обмитяването и всички митнически процедури.',
      icon: DocumentCheckIcon,
      color: 'bg-orange-500',
      lightColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    },
    {
      number: 8,
      title: 'Разтоварване в България',
      description: 'Автомобилът пристига в България и се разтоварва от контейнера в нашата база в София.',
      icon: HomeIcon,
      color: 'bg-rose-500',
      lightColor: 'bg-rose-50',
      textColor: 'text-rose-600'
    },
    {
      number: 9,
      title: 'Доставка до вас',
      description: 'Автомобилът се товари на платформа или автовоз и се транспортира до желана от вас точка.',
      icon: MapPinIcon,
      color: 'bg-pink-500',
      lightColor: 'bg-pink-50',
      textColor: 'text-pink-600'
    },
    {
      number: 10,
      title: 'Технотест',
      description: 'Съдействаме с технотест на вашия автомобил веднага след пристигането му в България.',
      icon: ClipboardDocumentCheckIcon,
      color: 'bg-teal-500',
      lightColor: 'bg-teal-50',
      textColor: 'text-teal-600'
    },
    {
      number: 11,
      title: 'Регистрация в КАТ',
      description: 'Предоставяме ви всички необходими документи за регистрация в КАТ.',
      icon: IdentificationIcon,
      color: 'bg-violet-500',
      lightColor: 'bg-violet-50',
      textColor: 'text-violet-600',
      highlight: '✅ Пълно съдействие'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold mb-3 md:mb-4">
              Как работи процесът?
            </h1>
            <p className="text-base sm:text-lg md:text-2xl text-primary-100 max-w-3xl mx-auto">
              От избора на автомобил до регистрацията - ние сме с вас на всяка стъпка
            </p>
          </div>
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 md:-mt-8">
        <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6">
          <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 md:p-6 border border-gray-100 text-center md:text-left">
            <div className="text-2xl sm:text-3xl mb-1 md:mb-2">🚗</div>
            <h3 className="font-bold text-gray-900 text-xs sm:text-sm md:text-base">Хиляди коли</h3>
            <p className="text-gray-600 text-xs mt-1 hidden sm:block">IAAI, Copart от САЩ и Канада</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 md:p-6 border border-gray-100 text-center md:text-left">
            <div className="text-2xl sm:text-3xl mb-1 md:mb-2">💰</div>
            <h3 className="font-bold text-gray-900 text-xs sm:text-sm md:text-base">Депозит 10%</h3>
            <p className="text-gray-600 text-xs mt-1 hidden sm:block">Връща се ако не спечелите</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 md:p-6 border border-gray-100 text-center md:text-left">
            <div className="text-2xl sm:text-3xl mb-1 md:mb-2">📅</div>
            <h3 className="font-bold text-gray-900 text-xs sm:text-sm md:text-base">2-3 месеца</h3>
            <p className="text-gray-600 text-xs mt-1 hidden sm:block">От аукциона до вас</p>
          </div>
        </div>
      </div>

      {/* Steps Timeline */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
        <div className="space-y-4 md:space-y-8">
          {steps.map((step, index) => (
            <div key={step.number} className="relative">
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="absolute left-8 top-20 w-0.5 h-full bg-gray-200 hidden md:block" />
              )}
              
              {/* Mobile: Compact Card */}
              <div className="md:hidden">
                <div className={`${step.lightColor} rounded-xl p-4 border border-gray-100 shadow-sm`}>
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-10 h-10 ${step.color} rounded-xl flex items-center justify-center shadow-md`}>
                      <step.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`${step.textColor} text-xs font-bold`}>
                          {step.number}.
                        </span>
                        <h3 className="text-sm font-bold text-gray-900">
                          {step.title}
                        </h3>
                      </div>
                      <p className="text-gray-600 text-xs leading-relaxed">
                        {step.description}
                      </p>
                      {step.highlight && (
                        <span className="inline-block mt-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-medium">
                          {step.highlight}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop: Full Card */}
              <div className="hidden md:flex flex-row gap-6">
                {/* Step Number & Icon */}
                <div className="flex-shrink-0">
                  <div className={`w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Content Card */}
                <div className={`flex-1 ${step.lightColor} rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`${step.textColor} text-sm font-bold bg-white px-3 py-1 rounded-full`}>
                      Стъпка {step.number}
                    </span>
                    {step.highlight && (
                      <span className="text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-medium">
                        {step.highlight}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Info Section */}
      <div className="bg-gray-900 text-white py-10 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-6 md:mb-8">Разпределение на плащанията</h2>
          
          {/* Mobile: Compact Timeline */}
          <div className="md:hidden space-y-3">
            <div className="bg-gray-800 rounded-xl p-4 flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
              <div>
                <h3 className="font-bold text-amber-400 text-sm">Депозит - 10%</h3>
                <p className="text-gray-400 text-xs">Връща се ако не спечелите</p>
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-xl p-4 flex items-start gap-3">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
              <div>
                <h3 className="font-bold text-green-400 text-sm">След спечелване</h3>
                <p className="text-gray-400 text-xs">Цена + такси + морски транспорт</p>
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-xl p-4 flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
              <div>
                <h3 className="font-bold text-blue-400 text-sm">След ~1 месец</h3>
                <p className="text-gray-400 text-xs">Мито и обмитяване</p>
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-xl p-4 flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">4</div>
              <div>
                <h3 className="font-bold text-purple-400 text-sm">1 седм. преди пристигане</h3>
                <p className="text-gray-400 text-xs">Транспорт от ЕС + разтоварване</p>
              </div>
            </div>
          </div>

          {/* Desktop: Full Cards */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1 - Deposit */}
            <div className="bg-gray-800 rounded-2xl p-6 relative">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                1
              </div>
              <div className="pt-4">
                <h3 className="font-bold text-xl text-amber-400 mb-2">Депозит</h3>
                <p className="text-2xl font-bold mb-3">10%</p>
                <p className="text-gray-400 text-sm">
                  При започване на процеса. Връща се ако не спечелите или се прехвърля за следващ автомобил.
                </p>
              </div>
            </div>

            {/* Step 2 - After Winning */}
            <div className="bg-gray-800 rounded-2xl p-6 relative">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                2
              </div>
              <div className="pt-4">
                <h3 className="font-bold text-xl text-green-400 mb-2">След спечелване:</h3>
                <p className="text-gray-300 text-sm mb-3">Веднага след търга</p>
                <ul className="text-gray-400 text-sm space-y-1">
                  <li>✓ Цена на автомобила</li>
                  <li>✓ Такси от търга</li>
                  <li>✓ Морски транспорт и контейнер*</li>
                </ul>
              </div>
            </div>

            {/* Step 3 - After 1 Month */}
            <div className="bg-gray-800 rounded-2xl p-6 relative">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                3
              </div>
              <div className="pt-4">
                <h3 className="font-bold text-xl text-blue-400 mb-2">След ~1 месец:</h3>
                <p className="text-gray-300 text-sm mb-3">При пристигане в Европа</p>
                <ul className="text-gray-400 text-sm space-y-1">
                  <li>✓ Мито и обмитяване</li>
                </ul>
              </div>
            </div>

            {/* Step 4 - 1 Week Before Arrival */}
            <div className="bg-gray-800 rounded-2xl p-6 relative">
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                4
              </div>
              <div className="pt-4">
                <h3 className="font-bold text-xl text-purple-400 mb-2">1 седмица преди:</h3>
                <p className="text-gray-300 text-sm mb-3">Финални разходи</p>
                <ul className="text-gray-400 text-sm space-y-1">
                  <li>✓ Транспорт от Холандия</li>
                  <li>✓ Разтоварване</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-6 md:mt-8 bg-gradient-to-r from-gray-800 to-gray-700 rounded-xl md:rounded-2xl p-4 md:p-6 text-center">
            <p className="text-gray-300 text-sm md:text-base">
              💡 Плащанията са разпределени - не плащате всичко наведнъж!
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 py-10 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
            Готови ли сте да започнете?
          </h2>
          <p className="text-sm sm:text-base md:text-xl text-gray-600 mb-6 md:mb-8">
            Свържете се с нас за безплатна консултация
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-primary-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg hover:shadow-xl text-sm md:text-base"
            >
              Свържете се с нас
              <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5 ml-2" />
            </Link>
            <Link
              to="/vehicles"
              className="inline-flex items-center justify-center bg-white text-primary-600 px-6 py-3 md:px-8 md:py-4 rounded-xl font-bold hover:bg-gray-50 transition-all shadow-lg border border-gray-200 text-sm md:text-base"
            >
              Разгледайте автомобили
            </Link>
          </div>
          
          <div className="mt-6 md:mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-gray-600 text-sm">
            <span className="flex items-center">
              <CheckCircleIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-1" />
              Безплатна консултация
            </span>
            <span className="flex items-center">
              <CheckCircleIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-1" />
              Без скрити такси
            </span>
          </div>
        </div>
      </div>

      {/* FAQ Preview */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center">Често задавани въпроси</h2>
        
        <div className="space-y-3 md:space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h3 className="font-bold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Какво ако не спечеля търга?</h3>
            <p className="text-gray-600 text-xs md:text-base">Депозитът се връща изцяло и може да опитате с друг автомобил.</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h3 className="font-bold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Колко време отнема процесът?</h3>
            <p className="text-gray-600 text-xs md:text-base">2-3 месеца от спечелването до получаване в България.</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h3 className="font-bold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Мога ли да участвам на живо?</h3>
            <p className="text-gray-600 text-xs md:text-base">Да! Можем да наддаваме заедно на живо или с автоматичен залог.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksPage;

